import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "../../services/toast";
import PhoneField from "../PhoneField";

import { makeStyles } from 'tss-react/mui';
import { green } from "@mui/material/colors";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { AuthContext } from "../../context/Auth/AuthContext";
import MenuItem from '@mui/material/MenuItem';
import useSettings from "../../hooks/useSettings";


import ContactCategoriSelect from "../ContactCategoriSelect";


import Autocomplete, {
	createFilterOptions,
} from '@mui/material/Autocomplete';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles()(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	row: {
		display: "flex",
		gap: theme.spacing(1.5),
		alignItems: "flex-start",
		[theme.breakpoints.down("sm")]: { flexDirection: "column", gap: 0 },
		"& > *": { flex: 1, minWidth: 0 },
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));


const filterOptions = createFilterOptions({
	trim: true,
});

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	number: Yup.string()
		.test("min-si-hay-valor", "Too Short!", (value) => !value || value.length >= 8)
		.max(50, "Too Long!")
		.test(
			"numero-o-username",
			"contactModal.form.numberOrUsernameRequired",
			function (value) {
				return Boolean(value || this.parent.username);
			}
		),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const { classes } = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		username: "",
		iduser: null
	};
	const { user } = useContext(AuthContext);
	const { profile } = user;

	const [contact, setContact] = useState(initialState);
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedUser, setSelectedUser] = useState(null);
	const [representative, setRepresentative] = useState("N/A");
	const [selectCat, setSelectCat] = useState(1);

	const [settings, setSettings] = useState({});
	const { getAll: getAllSettings } = useSettings();
	const [typeInfoContact, setTypeInfoContact] = useState([]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const fetchUsers = async (id) => {
		const { data } = await api.get("/users/" + id);
		setRepresentative(data.name);
	};

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setRepresentative("N/A");
		setSelectedUser(null);
		setSelectCat(null);
	};

	const handleSaveContact = async values => {
		try {
			if (selectCat != null) {
				values.idcategory = selectCat;
			}

			values.iduser = user.id;
			if (contactId) {
				await api.put(`/contacts/${contactId}`, values);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", values);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};



	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {

					setContact(data);
					if (data.iduser) {
						fetchUsers(data.iduser);
					}
					setSelectCat(data.idcategory);

				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	useEffect(() => {
		if (searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/", {
						params: { searchParam },
					});
					setOptions(data.users);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam]);

	useEffect(() => {
		async function findData() {
			const settingList = await getAllSettings();
			setSettings(settingList);
			if (Array.isArray(settingList)) {
				const typeInfoContact = settingList.find(
					(d) => d.key === "typeInfoContact"
				);
				if (typeInfoContact) {
					setTypeInfoContact(JSON.parse(typeInfoContact.value));
				}
			}
		}
		findData();
	}, []);


	return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<div className={classes.row}>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.name")}
										name="name"
										autoFocus
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									{}
									<PhoneField
										label={i18n.t("contactModal.form.number")}
										value={values.number}
										onChange={(v) => setFieldValue("number", v)}
										error={touched.number && Boolean(errors.number)}
										helperText={touched.number && errors.number}
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>


								</div>
								{values.username ? (
									<div>
										<TextField
											label={i18n.t("contactModal.form.username")}
											value={`@${values.username}`}
											disabled
											fullWidth
											margin="dense"
											variant="outlined"
											helperText={i18n.t("contactModal.form.usernameHelp")}
										/>
									</div>
								) : (
									<div>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.username")}
											name="username"
											fullWidth
											margin="dense"
											variant="outlined"
											helperText={i18n.t("contactModal.form.usernameManualHelp")}
										/>
									</div>
								)}
								<ContactCategoriSelect
									select={selectCat}
									onChange={(values) => setSelectCat(values)}

								/>
								<div>
									<Typography variant="subtitle1" style={{ marginBottom: 8, marginTop: 12 }}>
										{i18n.t("contactModal.representate.title")} : {representative}
									</Typography>
									{profile === "admin" || representative === "N/A" ? (
										<Autocomplete
											getOptionLabel={option => `${option.name}`}
											onChange={(e, newValue) => {
												setSelectedUser(newValue.id);
											}}
											options={options}
											filterOptions={filterOptions}
											autoHighlight
											noOptionsText={i18n.t("contactModal.representate.noOptions")}
											loading={loading}
											renderInput={params => (
												<TextField
													{...params}
													label={i18n.t("contactModal.representate.fieldLabel")}
													variant="outlined"
													onChange={e => setSearchParam(e.target.value)}
													slotProps={{
                                                        ...params.slotProps,

                                                        input: {
                                                            ...params.slotProps.input,
                                                            endAdornment: (
                                                                <React.Fragment>
                                                                    {loading ? (
                                                                        <CircularProgress color="inherit" size={20} />
                                                                    ) : null}
                                                                    {params.slotProps.input.endAdornment}
                                                                </React.Fragment>
                                                            ),
                                                        }
                                                    }}
												/>
											)}
										/>
									) : ''}


								</div>

								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
															select
														>
															{typeInfoContact.map((option) => (
																<MenuItem key={option.value} value={option.value}>
																	{option.value}
																</MenuItem>
															))}
														</Field>


														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
        </div>
    );
};

export default ContactModal;
